import { Box, Button, Paper, Typography } from "@mui/material";
import { ChromeIcon } from "components/common/SVG/ChromeIcon";
import { FeatureFlagsContext } from "contexts/FeatureFlagsContext";
import { useContext } from "react";
import { isFeatureEnabled, restrictToChromeFlag } from "utils/featureFlags";

/**
 * Props for the BrowserGuard component.
 */
interface BrowserGuardProps {
  children: React.ReactNode;
}

/**
 * Detects if the current browser is Chrome or Chrome-based.
 * @param userAgent - The user agent string to check (defaults to navigator.userAgent)
 * @returns true if the browser is Chrome or Chrome-based, false otherwise
 */
const isChromeOrChromeBased = (userAgent: string = navigator.userAgent): boolean => {
  const ua = userAgent.toLowerCase();

  // Chrome desktop detection (excluding other Chromium-based browsers)
  const isChromeDesktop =
    /chrome\/[\d.]+/.test(ua) &&
    !/edg|opr|brave|firefox|fxios|crios|vivaldi|ucbrowser|samsungbrowser|qqbrowser|duckduckgo|yabrowser/.test(ua);

  // Chrome on iOS (CriOS)
  const isChromeIOS = /crios\/[\d.]+/.test(ua);

  // Chrome on Android (excluding WebView)
  const isChromeAndroid = /chrome\/[\d.]+/.test(ua) && /android/.test(ua) && !/wv/.test(ua);

  return isChromeDesktop || isChromeIOS || isChromeAndroid;
};

const BrowserGuard: React.FC<BrowserGuardProps> = ({ children }) => {
  const flagsData = useContext(FeatureFlagsContext);
  const restrictToChrome = isFeatureEnabled(flagsData, restrictToChromeFlag);

  if (!restrictToChrome) {
    return <>{children}</>;
  }

  const isChrome = isChromeOrChromeBased();

  if (!isChrome) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: 2,
        }}>
        <Paper
          elevation={2}
          sx={{
            padding: 4,
            textAlign: "center",
            maxWidth: 500,
          }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Browser Not Supported
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This application is supported only on Chrome. Please switch to <span style={{ fontWeight: "bold" }}>Google Chrome </span> to
            continue.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unsupported browsers may cause functionality issues and a degraded user experience.
          </Typography>
          <Button
            startIcon={<ChromeIcon />}
            variant="contained"
            sx={{ mt: 3 }}
            onClick={() => window.open("https://www.google.com/chrome/", "_blank")}>
            Download Google Chrome
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};

export default BrowserGuard;
